import { Component, signal } from '@angular/core';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';

@Component({ template: '', standalone: true })
export class GameCardBase extends FikoYTKokBilesenComponent {
  kartStil: Record<string, string> = {};
  kenarGlow = '';
  gorunur = signal(false);
  private _gcObserver?: IntersectionObserver;

  constructor() { super(); }

  mouseMove(e: MouseEvent) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    this.kartStil = {
      transform: `perspective(600px) rotateY(${x * 15}deg) rotateX(${-y * 15}deg) scale(1.03)`,
      transition: 'transform 0.1s ease-out'
    };
    const glowX = ((e.clientX - rect.left) / rect.width) * 100;
    const glowY = ((e.clientY - rect.top) / rect.height) * 100;
    this.kenarGlow = `radial-gradient(circle at ${glowX}% ${glowY}%, rgba(var(--accent-rgb),.3) 0%, transparent 50%)`;
  }

  mouseLeave() {
    this.kartStil = { transform: 'perspective(600px) rotateY(0) rotateX(0) scale(1)', transition: 'transform 0.4s ease-out' };
    this.kenarGlow = '';
  }

  ngAfterViewInit() {
    this._gcObserver = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !this.gorunur()) { this.gorunur.set(true); this.CD.detectChanges(); }
    }, { threshold: 0.3 });
    this._gcObserver.observe(this.elementRef.nativeElement);
  }

  override ngOnDestroy() { super.ngOnDestroy(); this._gcObserver?.disconnect(); }
}

export const GC_BASE_CSS = `
:host { display: block; }

.gc {
  --accent: #00b4d8; --accent-rgb: 0,180,216;
  position: relative; border-radius: 4px; cursor: pointer;
  background: linear-gradient(165deg, rgba(var(--accent-rgb),.06) 0%, var(--py-arkaplan-yukselti, #0c0e14) 40%, var(--py-arkaplan-yukselti, #0c0e14) 100%);
  border: 1px solid rgba(var(--accent-rgb),.15);
  box-shadow: 0 8px 32px rgba(0,0,0,.35), 0 2px 8px rgba(0,0,0,.25), inset 0 1px 0 rgba(var(--accent-rgb),.08);
  overflow: hidden; will-change: transform;
  transform-style: preserve-3d;
  transition: box-shadow .4s cubic-bezier(.22,1,.36,1), border-color .4s;
}
.gc:hover {
  box-shadow: 0 20px 48px rgba(0,0,0,.5), 0 8px 32px rgba(var(--accent-rgb),.4), 0 0 60px rgba(var(--accent-rgb),.15), inset 0 1px 0 rgba(var(--accent-rgb),.3);
  border-color: rgba(var(--accent-rgb),.4);
}
.gc:hover .gc-shimmer { opacity: 1; animation: shimmer-slide 1.5s ease-in-out; }
.gc:hover .gc-baslik-cizgi { width: 100%; }

.gc-canli-bg {
  position: absolute; inset: 0; z-index: 0; border-radius: inherit;
  background: linear-gradient(135deg, rgba(var(--accent-rgb),.03) 0%, var(--py-arkaplan-yukselti, #0c0e14) 30%, var(--py-arkaplan-yukselti, #0c0e14) 70%, rgba(var(--accent-rgb),.05) 100%);
  animation: bg-rotate 8s linear infinite;
}
@keyframes bg-rotate {
  0% { filter: hue-rotate(0deg); }
  100% { filter: hue-rotate(360deg); }
}

.gc-parlak-kenar {
  position: absolute; top: 0; left: 0; right: 0; height: 1px; z-index: 5;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.12) 30%, rgba(255,255,255,.2) 50%, rgba(255,255,255,.12) 70%, transparent 100%);
}

.gc-kose { position: absolute; width: 18px; height: 18px; z-index: 5; border-color: rgba(var(--accent-rgb),.5); border-style: solid; transition: all .3s cubic-bezier(.22,1,.36,1); }
.gc-kose--sol-ust { top: 0; left: 0; border-width: 2px 0 0 2px; }
.gc-kose--sag-ust { top: 0; right: 0; border-width: 2px 2px 0 0; }
.gc-kose--sol-alt { bottom: 0; left: 0; border-width: 0 0 2px 2px; }
.gc-kose--sag-alt { bottom: 0; right: 0; border-width: 0 2px 2px 0; }
.gc:hover .gc-kose { width: 24px; height: 24px; border-color: rgba(var(--accent-rgb),.8); }

.gc-kose-ic { position: absolute; width: 10px; height: 10px; z-index: 5; border-color: rgba(var(--accent-rgb),.25); border-style: solid; transition: all .3s cubic-bezier(.22,1,.36,1); }
.gc-kose-ic--sol-ust { top: 4px; left: 4px; border-width: 1px 0 0 1px; }
.gc-kose-ic--sag-ust { top: 4px; right: 4px; border-width: 1px 1px 0 0; }
.gc-kose-ic--sol-alt { bottom: 4px; left: 4px; border-width: 0 0 1px 1px; }
.gc-kose-ic--sag-alt { bottom: 4px; right: 4px; border-width: 0 1px 1px 0; }
.gc:hover .gc-kose-ic { width: 14px; height: 14px; border-color: rgba(var(--accent-rgb),.5); }

.gc-rarity-serit {
  position: absolute; top: 0; left: 18px; right: 18px; height: 3px; z-index: 5;
  background: linear-gradient(90deg, transparent, var(--accent), transparent);
  box-shadow: 0 0 10px var(--accent), 0 2px 20px rgba(var(--accent-rgb),.3);
  animation: rarity-pulse 1.5s ease-in-out infinite;
}
@keyframes rarity-pulse { 0%,100% { opacity: .5; } 50% { opacity: 1; } }

.gc-ic-glow { position: absolute; inset: 0; z-index: 1; pointer-events: none; box-shadow: inset 0 0 30px rgba(var(--accent-rgb),.04); }

.gc-desen {
  position: absolute; inset: 0; z-index: 1; pointer-events: none; opacity: .04;
  background-image: radial-gradient(circle at 1px 1px, rgba(var(--accent-rgb),.3) 1px, transparent 0);
  background-size: 24px 24px;
}
.gc:hover .gc-desen { transform: scale(1.05); transition: transform .8s; }

.gc-shimmer {
  position: absolute; inset: 0; z-index: 6; pointer-events: none; opacity: 0;
  background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,.1) 45%, rgba(255,255,255,.25) 50%, rgba(255,255,255,.1) 55%, transparent 60%);
  background-size: 200% 100%; transition: opacity .3s;
}
@keyframes shimmer-slide { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.gc-holo {
  position: absolute; inset: 0; z-index: 7; pointer-events: none; opacity: 0;
  background: linear-gradient(
    105deg,
    transparent 20%,
    rgba(255,0,0,.08) 30%, rgba(255,255,0,.08) 40%,
    rgba(0,255,0,.08) 50%, rgba(0,255,255,.08) 60%,
    rgba(0,0,255,.08) 70%, rgba(255,0,255,.08) 80%,
    transparent 90%
  );
  background-size: 300% 300%;
  mix-blend-mode: overlay; border-radius: inherit;
  transition: opacity .3s;
}
.gc:hover .gc-holo {
  opacity: 1;
  animation: holo-shift 3s ease infinite;
}
@keyframes holo-shift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.gc-kenar-glow {
  position: absolute; inset: 0; z-index: 8; pointer-events: none; border-radius: inherit;
  opacity: 0; transition: opacity .3s;
}
.gc:hover .gc-kenar-glow { opacity: 1; }

.gc-icerik { position: relative; z-index: 3; padding: 14px; display: flex; flex-direction: column; gap: 6px; transition: transform .4s; }
.gc:hover .gc-icerik { transform: translateZ(8px); }

.gc-hud-baslik {
  display: flex; align-items: center; gap: 6px; margin-bottom: 2px;
  font-size: 8px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase;
  color: var(--accent); opacity: .7;
  font-family: 'Courier New', monospace;
}
.gc-hud-baslik::before {
  content: ''; width: 6px; height: 6px; border-radius: 1px;
  background: var(--accent); box-shadow: 0 0 6px var(--accent);
  animation: hud-dot-pulse 2s ease-in-out infinite;
}
.gc-hud-baslik::after {
  content: ''; flex: 1; height: 1px;
  background: linear-gradient(90deg, rgba(var(--accent-rgb),.3), transparent);
}
@keyframes hud-dot-pulse { 0%,100% { opacity: .5; } 50% { opacity: 1; } }
.gc:hover .gc-hud-baslik { opacity: 1; }

.gc-etiket { font-size: 8px; font-weight: 700; letter-spacing: .1em; padding: 2px 7px; border-radius: 2px; font-variant-numeric: tabular-nums; }
.gc-etiket--ust-sag {
  position: absolute; top: 8px; right: 10px; z-index: 5;
  background: rgba(var(--accent-rgb),.1); color: var(--accent);
  border: 1px solid rgba(var(--accent-rgb),.2); backdrop-filter: blur(4px);
  animation: badge-pulse 2s ease-in-out infinite;
}

.gc-baslik-cizgi {
  width: 40px; height: 1px;
  background: linear-gradient(90deg, transparent, var(--accent), transparent);
  transition: width .5s cubic-bezier(.22,1,.36,1);
}

.gc-stat-bolge {
  width: 100%; display: flex; flex-direction: column; gap: 6px;
  margin-top: 6px; padding-top: 8px;
  border-top: 1px solid rgba(var(--accent-rgb),.08);
}
.gc-stat-satir { display: flex; align-items: center; gap: 6px; }
.gc-stat-satir:hover .gc-stat-deger { transform: scale(1.3); text-shadow: 0 0 12px rgba(var(--accent-rgb),.6); }
.gc-stat-ikon {
  width: 18px; height: 18px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  border-radius: 3px; padding: 2px;
}
.gc-stat-ikon svg { width: 12px; height: 12px; animation: ikon-wobble 3s ease-in-out infinite; }
.gc-stat-ikon--turuncu { background: rgba(255,102,0,.1); color: #ff6600; }
.gc-stat-ikon--yesil { background: rgba(0,255,136,.08); color: #00ff88; }
.gc-stat-ikon--cyan { background: rgba(0,180,216,.08); color: #00b4d8; }
.gc-stat-isim { font-size: 8px; font-weight: 600; letter-spacing: .06em; color: var(--py-metin-ikincil, #64748b); width: 72px; flex-shrink: 0; }
.gc-stat-bar {
  flex: 1; height: 6px; border-radius: 3px;
  background: rgba(255,255,255,.06);
  box-shadow: inset 0 1px 2px rgba(0,0,0,.3);
  overflow: hidden;
}
.gc-stat-bar-dolgu {
  height: 100%; border-radius: 3px;
  transition: width 1.2s cubic-bezier(.22,1,.36,1);
  position: relative;
}
.gc-stat-bar-dolgu::after {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.3), transparent);
  animation: bar-parlama 2s ease-in-out infinite;
}
@keyframes bar-parlama { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
.gc-stat-bar--turuncu { background: linear-gradient(90deg, #ff6600, #ff9944); box-shadow: 0 0 8px rgba(255,102,0,.4); }
.gc-stat-bar--yesil { background: linear-gradient(90deg, #00cc66, #00ff88); box-shadow: 0 0 8px rgba(0,255,136,.4); }
.gc-stat-bar--cyan { background: linear-gradient(90deg, #0088aa, #00b4d8); box-shadow: 0 0 8px rgba(0,180,216,.4); }
.gc-stat-bar--mavi { background: linear-gradient(90deg, #2266cc, #4488ff); box-shadow: 0 0 8px rgba(68,136,255,.4); }
.gc-stat-bar--kirmizi { background: linear-gradient(90deg, #cc2222, #ef4444); box-shadow: 0 0 8px rgba(239,68,68,.4); }
.gc-stat-bar--sari { background: linear-gradient(90deg, #b8860b, #eab308); box-shadow: 0 0 8px rgba(234,179,8,.4); }
.gc-stat-deger {
  font-size: 12px; font-weight: 900; min-width: 28px; text-align: right;
  font-variant-numeric: tabular-nums; line-height: 1;
  transition: transform .2s, text-shadow .2s; transform-origin: right center;
}
.gc-stat-deger--turuncu { color: #ff6600; text-shadow: 0 0 8px rgba(255,102,0,.3); }
.gc-stat-deger--yesil { color: #00ff88; text-shadow: 0 0 8px rgba(0,255,136,.3); }
.gc-stat-deger--cyan { color: #00b4d8; text-shadow: 0 0 8px rgba(0,180,216,.3); }
.gc-stat-deger--mavi { color: #4488ff; text-shadow: 0 0 6px rgba(68,136,255,.3); }
.gc-stat-deger--kirmizi { color: #ef4444; text-shadow: 0 0 6px rgba(239,68,68,.3); }
.gc-stat-deger--sari { color: #eab308; text-shadow: 0 0 6px rgba(234,179,8,.3); }

@property --angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
@keyframes border-chase { to { --angle: 360deg; } }
.gc::before {
  content: ''; position: absolute; inset: -1px; z-index: 6; pointer-events: none;
  border-radius: inherit;
  background: conic-gradient(from var(--angle, 0deg), transparent 50%, var(--accent) 75%, transparent 100%);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude; -webkit-mask-composite: xor;
  padding: 2px;
  animation: border-chase 3s linear infinite;
}
.gc:hover::before { animation-duration: 1.5s; }

.gc-parcaciklar { position: absolute; inset: 0; z-index: 2; pointer-events: none; overflow: hidden; }
.gc-p {
  position: absolute; width: 2px; height: 2px; border-radius: 50%;
  background: var(--accent); opacity: .3; left: var(--x); top: var(--y);
  animation: float-p var(--d) ease-in-out infinite;
  box-shadow: 0 0 4px var(--accent);
}
.gc-p--buyuk { width: 3px; height: 3px; opacity: .4; }
.gc-p--kucuk { width: 1px; height: 1px; opacity: .6; }
@keyframes float-p {
  0%,100% { transform: translateY(0) scale(1); opacity: .3; }
  50% { transform: translateY(-20px) scale(2); opacity: .6; }
}
.gc:hover .gc-p { animation-duration: calc(var(--d) * 0.5) !important; box-shadow: 0 0 10px var(--accent); }

.gc-stat-bar-dolgu { animation: stat-flash 3s ease-in-out infinite; }
@keyframes stat-flash {
  0%,85%,100% { filter: brightness(1); }
  90% { filter: brightness(1.6); }
}

@keyframes badge-pulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(var(--accent-rgb),.4); }
  50% { box-shadow: 0 0 0 4px rgba(var(--accent-rgb),.0); }
}

.gc:hover .gc-baslik-ana { animation: baslik-glitch .3s ease-in-out; }
@keyframes baslik-glitch {
  0% { transform: translate(0); } 20% { transform: translate(-2px, 1px); }
  40% { transform: translate(2px, -1px); } 60% { transform: translate(-1px, 2px); }
  80% { transform: translate(1px, -2px); } 100% { transform: translate(0); }
}

@keyframes ikon-wobble {
  0%,100% { transform: rotate(0deg); }
  25% { transform: rotate(-6deg); }
  75% { transform: rotate(6deg); }
}

.gc-stat-deger { animation: deger-glow 4s ease-in-out infinite; }
@keyframes deger-glow {
  0%,100% { filter: brightness(1); }
  50% { filter: brightness(1.4); }
}

.gc-etiket:active, .bk-aciliyet-badge:active, .ie-oncelik:active, .kk-sonuc-damga:active, .sk-durum:active, .us-verim:active, .bn-kategori:active, .vd-vardiya:active, .mk-durum-chip:active { animation: badge-bounce .3s ease-out !important; }
@keyframes badge-bounce {
  0% { transform: scale(1); }
  30% { transform: scale(0.9); }
  60% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

.gc-xp-bar { height: 3px; background: rgba(255,255,255,.05); border-radius: 2px; margin-top: 8px; overflow: hidden; }
.gc-xp-dolgu { height: 100%; background: linear-gradient(90deg, var(--accent), gold); box-shadow: 0 0 6px var(--accent); border-radius: 2px; position: relative; }
.gc-xp-dolgu::after { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(255,255,255,.4), transparent); animation: bar-parlama 2s ease-in-out infinite; }

.gc-trend { font-size: 7px; margin-left: 2px; }
.gc-trend--yukari { color: #00ff88; }
.gc-trend--asagi { color: #ff4444; }

.gc-alt-ozet {
  display: flex; gap: 6px; align-items: center; flex-wrap: wrap;
  padding: 4px 0; border-top: 1px solid rgba(var(--accent-rgb), .1);
  animation: alt-ozet-giris .4s ease-out;
}
.gc-alt-badge {
  font-size: 8px; font-weight: 600; letter-spacing: .04em;
  padding: 1px 6px; border-radius: 2px;
  background: rgba(var(--accent-rgb), .08); color: var(--accent);
}
.gc-alt-son {
  font-size: 8px; color: var(--py-metin-ikincil, #64748b);
}
.gc-alt-ilerleme {
  flex: 1; height: 4px; border-radius: 2px; min-width: 40px;
  background: rgba(255,255,255,.06); overflow: hidden;
}
.gc-alt-ilerleme-dolgu {
  height: 100%; border-radius: 2px; background: var(--accent);
  transition: width .8s cubic-bezier(.22,1,.36,1);
}
@keyframes alt-ozet-giris { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
`;

export const GC_PARCACIKLAR_HTML = `
<div class="gc-parcaciklar">
  <span class="gc-p gc-p--buyuk" style="--d:7s;--x:15%;--y:25%"></span>
  <span class="gc-p" style="--d:11s;--x:75%;--y:35%"></span>
  <span class="gc-p gc-p--kucuk" style="--d:5s;--x:40%;--y:70%"></span>
  <span class="gc-p" style="--d:9s;--x:85%;--y:15%"></span>
  <span class="gc-p gc-p--buyuk" style="--d:13s;--x:30%;--y:85%"></span>
  <span class="gc-p gc-p--kucuk" style="--d:6s;--x:60%;--y:50%"></span>
</div>`;

export const GC_DEKOR_HTML = `
<div class="gc-canli-bg"></div>
<div class="gc-parlak-kenar"></div>
<div class="gc-kose gc-kose--sol-ust"></div>
<div class="gc-kose gc-kose--sag-ust"></div>
<div class="gc-kose gc-kose--sol-alt"></div>
<div class="gc-kose gc-kose--sag-alt"></div>
<div class="gc-kose-ic gc-kose-ic--sol-ust"></div>
<div class="gc-kose-ic gc-kose-ic--sag-ust"></div>
<div class="gc-kose-ic gc-kose-ic--sol-alt"></div>
<div class="gc-kose-ic gc-kose-ic--sag-alt"></div>
<div class="gc-rarity-serit"></div>
<div class="gc-ic-glow"></div>
<div class="gc-desen"></div>
<div class="gc-shimmer"></div>
<div class="gc-holo"></div>
<div class="gc-kenar-glow" [style.background]="kenarGlow"></div>`;
